/**
 * The sample's contact directory and notification rules.
 *
 * DEMONSTRATION records: the organisations are the kinds an airport deals
 * with, the people are roles rather than names, the numbers are Saudi-format
 * and the addresses example.com — so nothing here can reach anyone, and the
 * demonstration never sends a message in any case.
 */
import type { ContactOrg, NotifyRule } from "../domain/contacts";

let seq = 0;
const id = (p: string) => `${p}-${String(++seq).padStart(3, "0")}`;
const mob = (n: number, label: string, primary = false) => ({
  kind: "mobile" as const,
  value: `+96655${String(2000000 + n).slice(-7)}`,
  label,
  primary,
  notify: true,
});
const mail = (a: string, label: string, primary = false) => ({
  kind: "email" as const,
  value: `${a}@example.com`,
  label,
  primary,
  notify: true,
});
const land = (n: string, label: string) => ({
  kind: "phone" as const,
  value: `+966148${n}`,
  label,
  primary: true,
  notify: false,
});

export function mockDirectory(): ContactOrg[] {
  seq = 0;
  const org = (
    name: string,
    kind: ContactOrg["kind"],
    people: Omit<ContactOrg["people"][number], "id" | "orgId">[],
  ) => {
    const oid = id("org");
    return { id: oid, name, kind, active: true, people: people.map((p) => ({ ...p, id: id("person"), orgId: oid })) };
  };
  return [
    org("Tibah Airport Co. — Airport operations", "in_house", [
      {
        name: "Airport duty manager",
        role: "24-hour duty",
        language: "ar",
        preferred: "sms",
        active: true,
        hours: "24/7",
        points: [
          mob(1, "duty phone", true),
          mob(2, "backup"),
          mail("duty.manager", "duty inbox", true),
          mail("ops.control", "operations control"),
          land("400100", "AOCC desk"),
        ],
      },
      {
        name: "Airside operations supervisor",
        role: "Shift supervisor",
        language: "ar",
        preferred: "app",
        active: true,
        points: [mob(3, "shift phone", true), mail("airside.supervisor", "office", true)],
      },
    ]),
    org("AGL maintenance contractor", "contractor", [
      {
        name: "AGL duty engineer",
        role: "Call-out engineer",
        language: "en",
        preferred: "sms",
        active: true,
        hours: "24/7 call-out",
        points: [
          mob(10, "call-out phone", true),
          mob(11, "engineer's own"),
          mail("agl.callout", "call-out inbox", true),
          mail("agl.contract.manager", "contract manager"),
        ],
      },
      {
        name: "AGL contract manager",
        role: "Contract manager",
        language: "en",
        preferred: "email",
        active: true,
        hours: "Sun–Thu 07:00–16:00",
        points: [mob(12, "mobile", true), mail("agl.manager", "office", true)],
      },
    ]),
    org("Airside sweeping services", "contractor", [
      {
        name: "Sweeping supervisor",
        role: "Supervisor",
        language: "ar",
        preferred: "sms",
        active: true,
        points: [mob(20, "supervisor", true), mail("sweeping.supervisor", "office", true)],
      },
    ]),
    org("Pavement and civil works contractor", "contractor", [
      {
        name: "Civil works site engineer",
        role: "Site engineer",
        language: "en",
        preferred: "email",
        active: true,
        points: [
          mob(30, "site phone", true),
          mail("civil.site", "site office", true),
          mail("civil.projects", "projects"),
        ],
      },
    ]),
    org("Airport security", "agency", [
      {
        name: "Security control room",
        role: "Control room",
        language: "ar",
        preferred: "sms",
        active: true,
        hours: "24/7",
        points: [
          mob(40, "control room", true),
          land("400200", "control room desk"),
          mail("security.control", "control room", true),
        ],
      },
    ]),
    org("Civil Defence — Madinah", "authority", [
      {
        name: "Civil Defence duty officer",
        role: "Duty officer",
        language: "ar",
        preferred: "sms",
        active: true,
        points: [land("998000", "emergency line"), mob(50, "liaison mobile", true)],
      },
    ]),
    org("Saudia — station operations", "airline", [
      {
        name: "Station duty officer",
        role: "Station control",
        language: "en",
        preferred: "email",
        active: true,
        points: [mob(60, "station control", true), mail("station.control", "station control", true)],
      },
    ]),
    /* The contractors the sample's work orders name (mock/data.ts CLASS). "Landside services
       contractor" is left out on purpose: dispatching its orders shows what "not in the
       directory" looks like. */
    org("Airfield maintenance contractor", "contractor", [
      {
        name: "Airfield maintenance supervisor",
        role: "Supervisor",
        language: "en",
        preferred: "app",
        active: true,
        points: [mob(70, "duty phone", true), mail("airfield.maintenance", "office", true)],
      },
    ]),
    org("Terminal facilities contractor", "contractor", [
      {
        name: "Terminal FM duty engineer",
        role: "Duty engineer",
        language: "en",
        preferred: "app",
        active: true,
        points: [mob(71, "duty phone", true), mail("terminal.fm", "office", true)],
      },
    ]),
    org("Terminal cleaning", "contractor", [
      {
        name: "Cleaning shift supervisor",
        role: "Shift supervisor",
        language: "ar",
        preferred: "app",
        active: true,
        points: [mob(72, "duty phone", true), mail("cleaning.shift", "office", true)],
      },
    ]),
    org("Landscape contractor", "contractor", [
      {
        name: "Landscape foreman",
        role: "Foreman",
        language: "ar",
        preferred: "app",
        active: true,
        points: [mob(73, "duty phone", true), mail("landscape", "office", true)],
      },
    ]),
    org("Environmental services contractor", "contractor", [
      {
        name: "Environmental services supervisor",
        role: "Supervisor",
        language: "en",
        preferred: "app",
        active: true,
        points: [mob(74, "duty phone", true), mail("environment", "office", true)],
      },
    ]),
  ];
}

export function mockRules(siteId: string, dir: ContactOrg[]): NotifyRule[] {
  const person = (name: string) => dir.flatMap((o) => o.people).find((p) => p.name === name)?.id ?? "";
  const org = (name: string) => dir.find((o) => o.name === name)?.id ?? "";
  return [
    {
      id: "rule-001",
      siteId,
      name: "Every P1 — the duty manager and airside operations",
      active: true,
      events: ["created", "dispatched", "completed", "cancelled", "overdue"],
      match: { priorities: [1] },
      recipients: [
        { type: "person", id: person("Airport duty manager") },
        { type: "person", id: person("Airside operations supervisor") },
      ],
      channels: ["sms", "email", "app"],
      escalateAfterMin: 15,
      escalateTo: [{ type: "person", id: person("Airport duty manager") }],
    },
    {
      id: "rule-002",
      siteId,
      name: "Lighting faults — the AGL contractor",
      active: true,
      events: ["created", "dispatched", "overdue"],
      match: { dispatchGroups: ["AGL1"] },
      recipients: [{ type: "org", id: org("AGL maintenance contractor") }],
      channels: ["sms", "email"],
      escalateTo: [],
    },
    {
      id: "rule-003",
      siteId,
      name: "Sweeper call-outs — the sweeping contractor",
      active: true,
      events: ["dispatched", "cancelled"],
      match: { dispatchGroups: ["SWP1"] },
      recipients: [{ type: "org", id: org("Airside sweeping services") }],
      channels: ["sms"],
      escalateTo: [],
    },
  ];
}
